import type { ReactNode } from 'react';
import { faGripVertical } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

interface ListItemProps {
  label: ReactNode;
  action: ReactNode;
  body?: ReactNode;
  className?: string;
  hideDragHandle?: boolean;
}

interface SortableListItemProps extends ListItemProps {
  id: string;
}

export interface GroupRow {
  id: string;
  label: ReactNode;
  action: ReactNode;
  body?: ReactNode;
}

interface SortableGroupListItemProps {
  id: string;
  rows: GroupRow[];
  className?: string;
}

function DragHandle({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('touch-none flex items-center justify-center size-8 shrink-0', className)}
    >
      <Icon icon={faGripVertical} className="size-3.5" />
    </div>
  );
}

export function StaticListItem({
  label,
  action,
  body,
  className,
  hideDragHandle = false,
}: ListItemProps) {
  const row = (
    <>
      {/* Hidden handle keeps its box so labels stay aligned with the sortable rows below. */}
      <DragHandle
        className={cn(
          'cursor-default text-muted-foreground/40 pointer-events-none',
          hideDragHandle && 'invisible',
        )}
      />
      <span className="flex-1 text-sm font-medium">{label}</span>
      {action}
    </>
  );

  if (!body) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border bg-background p-2 select-none',
          className,
        )}
      >
        {row}
      </div>
    );
  }

  return (
    <div className={cn('rounded-md border bg-background select-none', className)}>
      <div className="flex items-center gap-2 py-1">{row}</div>
      {body}
    </div>
  );
}

export function SortableListItem({ id, label, action, body, className }: SortableListItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const row = (
    <>
      <Button
        variant="ghost"
        size="icon-sm"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        aria-pressed={isDragging}
        className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground"
      >
        <Icon icon={faGripVertical} className="size-3.5" />
      </Button>
      <span className="flex-1 text-sm font-medium">{label}</span>
      {action}
    </>
  );

  const style = { transform: CSS.Transform.toString(transform), transition };

  if (!body) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'flex items-center gap-2 rounded-md border bg-background p-2 select-none',
          isDragging && 'opacity-50 shadow-lg',
          className,
        )}
      >
        {row}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-md border bg-background select-none',
        isDragging && 'opacity-50 shadow-lg',
        className,
      )}
    >
      <div className="flex items-center gap-2 py-1">{row}</div>
      {body}
    </div>
  );
}

export function SortableGroupListItem({ id, rows, className }: SortableGroupListItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-md border bg-background p-2 select-none',
        isDragging && 'opacity-50 shadow-lg',
        className,
      )}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        aria-pressed={isDragging}
        className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground self-center"
      >
        <Icon icon={faGripVertical} className="size-3.5" />
      </Button>
      <div className="flex-1 divide-y divide-border/50">
        {rows.map((row) => (
          <div key={row.id} className="py-1.5 first:pt-0 last:pb-0">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{row.label}</span>
              {row.action}
            </div>
            {row.body ? <div className="pt-2.5">{row.body}</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}
